package kz.dentalcrm.app.screens.main

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.NavigationRailItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.window.core.layout.WindowSizeClass.Companion.WIDTH_DP_MEDIUM_LOWER_BOUND
import compose.icons.FeatherIcons
import compose.icons.feathericons.List
import compose.icons.feathericons.MoreHorizontal
import compose.icons.feathericons.UserPlus
import compose.icons.feathericons.Users
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.appointments
import dentalcrm.composeapp.generated.resources.employees
import dentalcrm.composeapp.generated.resources.login_sub_title
import dentalcrm.composeapp.generated.resources.login_title
import dentalcrm.composeapp.generated.resources.menu
import dentalcrm.composeapp.generated.resources.patients
import kotlinx.collections.immutable.persistentListOf
import kz.dentalcrm.app.core.ui.components.ContentButton
import kz.dentalcrm.app.screens.appointments.AppointmentsScreen
import kz.dentalcrm.app.screens.employees.EmployeesScreen
import kz.dentalcrm.app.screens.main.mvi.MainViewEvent
import kz.dentalcrm.app.screens.menu.MenuScreen
import kz.dentalcrm.app.screens.patients.PatientsScreen
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

data class MainTab(
    val route: String,
    val title: String,
    val icon: ImageVector,
    val content: @Composable () -> Unit
)

@Composable
fun MainScreen(
    viewModel: MainViewModel = koinViewModel()
) {
    val state by viewModel.viewState.collectAsState()

    val appointmentsTitle = stringResource(Res.string.appointments)
    val employeesTitle = stringResource(Res.string.employees)
    val patientsTitle = stringResource(Res.string.patients)
    val menuTitle = stringResource(Res.string.menu)

    val tabs = remember(appointmentsTitle, employeesTitle, patientsTitle, menuTitle) {
        persistentListOf(
            MainTab(
                route = "appointments",
                title = appointmentsTitle,
                icon = FeatherIcons.List,
                content = { AppointmentsScreen() }
            ),
            MainTab(
                route = "employees",
                title = employeesTitle,
                icon = FeatherIcons.Users,
                content = { EmployeesScreen() }
            ),
            MainTab(
                route = "patients",
                title = patientsTitle,
                icon = FeatherIcons.UserPlus,
                content = { PatientsScreen() }
            ),
            MainTab(
                route = "menu",
                title = menuTitle,
                icon = FeatherIcons.MoreHorizontal,
                content = { MenuScreen() }
            )
        )
    }

    val windowSizeClass = currentWindowAdaptiveInfo().windowSizeClass
    val isExpandedScreen = windowSizeClass.isWidthAtLeastBreakpoint(WIDTH_DP_MEDIUM_LOWER_BOUND)

    if (isExpandedScreen) {
        ExpandedLayout(
            tabs = tabs,
            selectedTab = tabs[state.selectedTabIndex],
            onTabSelected = { tab ->
                viewModel.setEvent(MainViewEvent.OnTabSelected(tabs.indexOf(tab)))
            }
        )
    } else {
        CompactLayout(
            tabs = tabs,
            selectedTab = tabs[state.selectedTabIndex],
            onTabSelected = { tab ->
                viewModel.setEvent(MainViewEvent.OnTabSelected(tabs.indexOf(tab)))
            }
        )
    }
}

@Composable
private fun CompactLayout(
    tabs: List<MainTab>,
    selectedTab: MainTab,
    onTabSelected: (MainTab) -> Unit
) {
    Scaffold(
        containerColor = DefaultTheme.colors.surfaceBackground,
        contentColor = DefaultTheme.colors.onSurface,
        content = {
            selectedTab.content()
        },
        bottomBar = {
            NavigationBar(
                containerColor = DefaultTheme.colors.surface,
                contentColor = DefaultTheme.colors.onSurface
            ) {
                tabs.forEach { tab ->
                    TabNavigationItem(tab, selectedTab, onTabSelected)
                }
            }
        }
    )
}

@Composable
private fun ExpandedLayout(
    tabs: List<MainTab>,
    selectedTab: MainTab,
    onTabSelected: (MainTab) -> Unit
) {
    Row(modifier = Modifier.fillMaxSize()) {
        Column(
            modifier = Modifier
                .background(color = DefaultTheme.colors.surface)
                .fillMaxHeight()
                .statusBarsPadding()
                .widthIn(min = 96.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Top
        ) {
            Column(
                modifier = Modifier.padding(horizontal = 16.dp)
                    .padding(top = 32.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    textAlign = TextAlign.Start,
                    text = stringResource(Res.string.login_title),
                    style = DefaultTheme.typography.heading2,
                    color = DefaultTheme.colors.onSurface
                )
                Spacer(modifier = Modifier.height(8.dp))
            }
            Spacer(modifier = Modifier.height(56.dp))
            Column {
                Spacer(modifier = Modifier.height(16.dp))
                tabs.forEach { tab ->
                    ContentButton(
                        modifier = Modifier
                            .padding(horizontal = 8.dp),
                        onClick = {
                            onTabSelected(tab)
                        },
                        shape = RoundedCornerShape(24.dp),
                        colors = ButtonDefaults.buttonColors().copy(
                            containerColor = Color.Transparent,
                        )
                    ) {
                        Row(
                            modifier = Modifier.widthIn(min = 124.dp)
                                .height(56.dp)
                                .padding(horizontal = 64.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                imageVector = tab.icon,
                                contentDescription = tab.title,
                                tint = if (selectedTab == tab) {
                                    DefaultTheme.colors.primary
                                } else {
                                    DefaultTheme.colors.onSurfaceVariant
                                }
                            )
                            Spacer(modifier = Modifier.widthIn(8.dp))
                            Text(
                                text = tab.title,
                                style = DefaultTheme.typography.captionMedium,
                                color = if (selectedTab == tab) {
                                    DefaultTheme.colors.primary
                                } else {
                                    DefaultTheme.colors.onSurfaceVariant
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(16.dp))
            }
        }

        // Content
        Box(
            modifier = Modifier
                .background(DefaultTheme.colors.surfaceBackground)
                .weight(1f)
                .fillMaxHeight()
        ) {
            selectedTab.content()
        }
    }
}

@Composable
fun RowScope.TabNavigationItem(
    tab: MainTab,
    selectedTab: MainTab,
    onTabSelected: (MainTab) -> Unit
) {
    NavigationBarItem(
        selected = selectedTab == tab,
        colors = NavigationBarItemDefaults.colors(
            selectedTextColor = DefaultTheme.colors.primary,
            selectedIconColor = DefaultTheme.colors.primary,
            indicatorColor = Color.Transparent,
            disabledIconColor = DefaultTheme.colors.onSurface,
            disabledTextColor = DefaultTheme.colors.onSurface,
            unselectedTextColor = DefaultTheme.colors.onSurfaceVariant,
            unselectedIconColor = DefaultTheme.colors.onSurfaceVariant,
        ),
        onClick = { onTabSelected(tab) },
        icon = {
            Icon(
                imageVector = tab.icon,
                contentDescription = tab.title
            )
        },
        label = {
            Text(
                text = tab.title,
                style = DefaultTheme.typography.captionMedium
            )
        }
    )
}
